<template>
    <div
        class="filter-companions py-2 select-none"
        :data-filter-reference="filters.reference"
        :data-selected-value="companionsString"
    >
        <div class="p-4 pt-0" v-if="!! infoText">
            {{ infoText ?? ''}} <template v-if="this.overflowCompanions.babies > 0">{{ infoTextBabies ?? '' }}</template>
        </div>
        <div class="filter-companion" v-for="companion in availableCompanions">

            <div v-html="companion.label"></div>
            <label :for="companion.name"></label>

            <div class="flex items-center touch-manipulation" v-if="companion.available && companion.allowed">
                <NumberStepper
                    :name="companion.name"
                    :quantity="companion.quantity"
                    :on-can-decrease="() => canDecrease(companion)"
                    :on-can-increase="() => canIncrease(companion)"
                    :on-change="(value: number) => onInputChange(value, companion)"
                    :on-enter="updateFilters"
                ></NumberStepper>

                <div
                    class="flex items-center justify-center mx-1.5 mr-1 w-6 h-12 cursor-pointer hover:opacity-90 bzg-hh-orange touch-manipulation"
                    @click.prevent="clearCompanion(companion)"
                >
                    <img class="w-3 h-3" src="/assets/img/frontend-v3/icons/global/icon-x-grey-700.svg" alt="x">
                </div>
            </div>
            <div v-else-if="! companion.allowed" v-html="translations.disallowed || 'DISALLOWED'" class="flex h-12 items-center opacity-75"></div>
            <div v-else v-html="translations.unavailable || 'UNAVAILABLE'" class="flex h-12 items-center opacity-75"></div>
        </div>
    </div>

</template>

<script lang="ts">
import { defineComponent } from "vue";
import NumberStepper from "../../Form/NumberStepper.vue";

// noinspection TypeScriptValidateTypes
export default defineComponent({

    name: "Companions",
    components: {NumberStepper},

    props: [
        'controlMode',
        'filterData',
        'maxData',
        'overflowData',
        'petData',
        'babyData',
        'infoText',
        'infoTextBabies'
    ],

    data() {
        return {

            mode: 'direct',//or button
            filters: null,

            // Default companion structure
            /** @type {Set<Companion>} */
            companions: new Set([
                new Companion('adults'),
                new Companion('children'),
                new Companion('babies'),
                new Companion('pets'),
            ]),

            // A list of the named companions (adults, children, babies and pets)
            /** @type {{[string]: Companion}} */
            mappedCompanions: {},

            maxCompanions: {
                people: 99,
                adults: 99,
                children: 98,
                babies: 98,
                pets: 4,
            },

            pets: {
                allowed: true,
                available: true,
            },

            babies: {
                allowed: true,
            },

            overflowCompanions: {
                babies: 2,
            },

            filterTimeout: null,
            updateFiltersDelay: 1000,

            translations: {},
        }
    },

    created() {
        this.pets = this.petData ?? this.pets;
        this.babies = this.babyData ?? this.babies;

        this.mode = this.controlMode ?? this.mode;

        this.filters = this.filterData;
        this.translations = this.filterData.translations;

        this.createCompanionList();

        this.maxCompanions.people = this.maxData?.people ?? this.maxCompanions.people;
        this.maxCompanions.adults = this.maxData?.adults ?? this.maxCompanions.adults;
        this.maxCompanions.children = this.maxData?.children ?? this.maxCompanions.children;
        this.maxCompanions.babies = this.maxData?.babies ?? this.maxCompanions.babies;
        this.maxCompanions.pets = this.maxData?.pets ?? this.maxCompanions.pets;

        if (typeof(this.overflowData) === 'object') {
            Object.keys(this.overflowData).forEach((key) => {
                this.overflowCompanions[key] = this.overflowData[key];
            });
        }
    },

    mounted() {
        this.buildCountFromFilters();

        this.Bus.on('clear-primary-filter', (reference) => {
            if (reference === 'companions') {
                if (this.getTotal() > 0) {
                    this.clearAllAndUpdate();
                }
            }
        });

        this.Bus.on('clear-filter', (data) => {
            // Hack: button to clear company 'as a filter' is quite retarded
            if (data.reference === 'companions' || data.baseCategory === 'travel-company') {
                this.clearAll();
                this.Bus.emit('companions-change', this.companionsUrlSegment);
            }
        });

        this.Bus.on('apply-filter', (reference) => {
            if (reference === 'companions') {
                this.updateFilters();
            }
        });

        this.Bus.on('popup-handle-apply', (reference) => {
            if (reference === 'companions') {
                this.Bus.emit('close-popup', 'companions');
                this.updateFilters();
            }
        });

        this.Bus.on('popup-handle-clear', (reference) => {
            if (reference === 'companions') {
                this.clearAll();
            }
        });

        this.Bus.on('popup-closed', (reference) => {
            if (reference === 'companions') {
                this.updateFilters();
            }
        });

    },

    watch: {
        filters: function(newFilters) {
            this.filters = newFilters;

            this.createCompanionList();
        },
    },

    methods: {
        createCompanionList() {
            if (Array.isArray(this.filters.companions)) {
                // Clear the list of companions
                this.companions.clear();

                // Create a new companion class for each name, and set their labels
                this.filters.companions.forEach(companion =>
                    this.companions.add(
                        new Companion(
                            companion.reference,
                            companion.maxQuantity,
                            companion.overflow,
                            this.translations.labels[companion.reference],
                            companion.available,
                            companion.quantity || 0,
                        )
                    )
                );
            } else {
                // Set the labels for the default companions
                this.companions.forEach(companion => {
                    companion.label = this.translations.labels[companion.name];
                    companion.maxQuantity = this.maxCompanions[companion.name];
                    companion.overflow = this.overflowCompanions[companion.name] || 0;
                });
            }

            this.mappedCompanions = {};
            this.companions.forEach(companion => {
                if (['adults', 'children', 'babies', 'pets'].includes(companion.name)) {
                    this.mappedCompanions[companion.name] = companion;
                }
            });

            // Disable the pets and babies if they're not allowed.
            if (this.mappedCompanions.pets) {
                if (! this.pets.allowed) {
                    this.mappedCompanions.pets.allowed = false;
                } else if (! this.pets.available) {
                    this.mappedCompanions.pets.available = false;
                }
            }
            if (! this.babies.allowed) {
                if (this.mappedCompanions.babies) {
                    this.mappedCompanions.babies.allowed = false;
                }
                if (this.mappedCompanions.children) {
                    this.mappedCompanions.children.allowed = false;
                }
            }
        },

        /**
         * Returns if the companion can be decreased.
         */
        canDecrease(companion : Companion) : boolean
        {
            return companion.quantity > 0;
        },

        /**
         * Returns if the companion can be increased
         */
        canIncrease(companion : Companion) : boolean
        {
            return this.numRemaining(companion) > 0;
        },

        updateFilters() {
            this.$nextTick(() => {
                this.Bus.emit('filter-change');
                this.Bus.emit('companions-change', this.companionsUrlSegment);
            })
        },

        clearFilterTimeout() {
            clearTimeout(this.filterTimeout);
        },

        /**
         * Returns how many you can still add to this companion.
         */
        numRemaining(companion: Companion) : number
        {
            // Get this out of the way; you cannot add more if you're over your own budget.
            if (companion.quantity >= companion.maxQuantity)
                return companion.maxQuantity - companion.quantity;

            // Easy solution for pets.
            if (companion.name === 'pets')
                return companion.maxQuantity - companion.quantity;

            // This is how many fit in the house (house maximum). Increase by this companion's overflow
            const houseTotal = this.maxCompanions.people + companion.overflow;

            // Count the companions, subtract their overflows. Subtract houseTotal, and you will get
            // your budget.
            const sum = Array.from(this.companions)
                .filter((c : any) => c.name !== 'pets') // don't count pets
                .filter((c : any) => c !== companion) // skip this companion
                    // sum the quantities, but reduce by their overflow.
                .reduce((collector : number, c : any) => Math.max(0, c.quantity - c.overflow) + collector, 0)
                 + companion.quantity; // Add the quantity of the selected companion, ignore overflow

            return houseTotal - sum;
        },

        stopInterval() {
            clearTimeout(this.intervalStartup);
            clearInterval(this.interval);
        },

        getTotalPeople() : number
        {
            // @ts-ignore
            return Array.from(this.companions)
                // @ts-ignore
                .filter(companion => companion.name !== 'pets')
                // @ts-ignore
                .reduce((prev, companion) => prev + companion.quantity, 0);
        },

        getTotalBabies() : number
        {
            return this.mappedCompanions.babies?.quantity || 0;
        },

        getTotalPets() : number
        {
            return this.mappedCompanions.pets?.quantity || 0;
        },

        getTotal() : number
        {
            // @ts-ignore
            return Array.from(this.companions)
                // @ts-ignore
                .reduce((total, companion) => total + companion.quantity, 0);
        },

        setCount(companion : Companion, count : number) {
            companion.quantity = count;

            const numRemaining = this.numRemaining(companion);

            if (numRemaining < 0) {
                companion.quantity = count + numRemaining;
            }

            if (companion.quantity < 0) {
                companion.quantity = 0;
            }

            if (! companion.allowed || ! companion.available) {
                companion.quantity = 0;
            }
        },

        buildCountFromFilters() {
            const countArray = this.filterData.id?.split(',');

            if (this.mappedCompanions.adults) {
                this.setCount(this.mappedCompanions.adults, Number(countArray[0] ?? this.mappedCompanions.adults?.quantity ?? 0));
            }

            if (this.mappedCompanions.children) {
                this.setCount(this.mappedCompanions.children, Number(countArray[1] ?? this.mappedCompanions.children?.quantity ?? 0));
            }

            if (this.mappedCompanions.babies) {
                this.setCount(this.mappedCompanions.babies, Number(countArray[2] ?? this.mappedCompanions.babies?.quantity ?? 0));
            }

            if (this.mappedCompanions.pets) {
                this.setCount(this.mappedCompanions.pets, Number(countArray[3] ?? this.mappedCompanions.pets?.quantity ?? 0));
            }
        },

        onInputChange(value : number, companion : Companion) {
            this.Bus.emit('companion-touched');
            this.setCount(companion, value);
            this.clearFilterTimeout();

            if (this.mode === 'direct') {
                this.filterTimeout = setTimeout(() => {
                    this.updateFilters();
                }, this.updateFiltersDelay);
            }
        },

        clearAll() {
            this.companions.forEach(companion => this.setCount(companion, 0));
        },

        clearAllAndUpdate() {
            this.clearAll();

            if (this.mode === 'direct') {
                this.updateFilters();
            }
        },

        clearCompanion(companion : Companion) {
            this.setCount(companion, 0);

            if (this.mode === 'direct') {
                this.updateFilters();
            }
        }
    },

    computed: {
        availableCompanions() {
            return this.companions;
        },

        companionsString() {
            return this.getTotal() === 0 ? '' : Object.values(this.mappedCompanions).map(companion => companion.quantity).join(',');
        },

        companionsUrlSegment() {
            if (this.filters.reference && this.companionsString) {
                return this.filters.reference + ':' + this.companionsString;
            }

            return null;
        },

    },
});

class Companion {
    public readonly name: string;
    public quantity: number;
    public maxQuantity: number;
    public overflow: number;
    public translations: any;
    public readonly available: boolean;
    public allowed: boolean;
    public label: string;

    constructor(name, maxQuantity = 0, overflow = 0, label = {}, available = true, initialQuantity = 0) {
        this.name = name;
        this.quantity = initialQuantity;
        this.overflow = overflow;
        this.translations = label;
        this.available = available;
        this.maxQuantity = maxQuantity;
        this.allowed = true;

        this.buildLabel();
    }

    buildLabel() {
        this.label = `<span class="block sm:inline-block sm:w-32">${this.translations.name}</span>`;

        if (this.translations.ageDescription) {
            this.label += `<span class="text-hh-grey-700">${this.translations.ageDescription}</span>`;
        }
    }

    getAgeShort() {
        if (this.translations.minAge !== null && this.translations.maxAge !== null) {
            return this.translations.minAge + ' - ' + this.translations.maxAge;
        }

        if (this.translations.minAge !== null) {
            return this.translations.minAge + '+';
        }

        if (this.translations.maxAge !== null) {
            return '-' + this.translations.maxAge;
        }

        return '';
    }
}
</script>

